import { Box, ButtonV2, useToast } from '@withjoy/joykit';
import type { FC } from 'react';
import React, { useCallback, useRef } from 'react';
import { StyledImage, styles } from './EventPhoto.styles';
import { TempSpinner } from '../../../../../../../shared/components/TempSpinner';
import { PhotoMenu } from '../../../../../../../shared/components/PhotoMenu';
import { ReactComponent as CameraIcon } from '@assets/icons/camera-outline.svg';
import { useRegistryGuestTranslations } from '@apps/registry/guest/routes/GuestRegistry/GuestRegistry.i18n';
import { useFilestack } from '@shared/utils/filestack';
import { useRemoveEventPagePhotoMutation } from '@graphql/mutations/registry.graphql.generated';
import { useUploadPhotoFromUrlMutation } from '@shared/components/PhotoUpload/useContainerPhotoUpload.graphql.generated';
import { useSaveNativeEventPagePhoto } from '@shared/components/PhotoUpload/useSaveNativeEventPagePhoto';
import { useUpdateRegistryPageContainerPhotoMutation } from './EventPhoto.graphql.generated';
import type { ApolloCache } from '@apollo/client';
import { IconCard } from '../../IconCard';
import { Photos } from '@withjoy/joykit/icons';

type Props = {
  eventPageId?: string;
  // PRNT-4193 — when set, the registry page is migrated and writes route through
  // `updatePageContainer` (after `uploadPhotoFromUrl`) instead of legacy
  // `setEventPageAsset` / `removeEventPagePhoto`.
  migratedPageContainerId?: string;
  handleImageLoad?(): void;
  onUpdatePhoto?(previewUrl?: string): void;
  onDeletePhoto?(): void;
  photo?: string;
  showEdit?: boolean;
  firebaseId?: string;
  showEmptyCard?: boolean;
};

export const EventPhoto: FC<Props> = ({
  eventPageId,
  migratedPageContainerId,
  handleImageLoad,
  photo,
  showEdit = false,
  onDeletePhoto,
  onUpdatePhoto,
  firebaseId = '',
  showEmptyCard = false,
}) => {
  const isMigrated = !!migratedPageContainerId;
  const hasTarget = isMigrated || !!eventPageId;
  const canEdit = showEdit && hasTarget;
  const photoRef = useRef<HTMLImageElement>(null);
  const { getPhotoMenuTranslations } = useRegistryGuestTranslations();

  const { toast } = useToast();
  const { error, success } = getPhotoMenuTranslations();

  const updateLegacyPhotoInCache = useCallback(
    ({ cache, url }: { cache: ApolloCache<unknown>; url: string | null }) => {
      cache.modify({
        id: cache.identify({ __typename: 'EventPage', id: eventPageId }),
        fields: {
          photoV2(existingPhotoRef) {
            return url
              ? {
                  ...existingPhotoRef,
                  url: url,
                }
              : null;
          },
        },
      });
    },
    [eventPageId],
  );

  const { saveNativeEventPagePhoto, isSavingNativePagePhoto: isUpdatingLegacyPhoto } = useSaveNativeEventPagePhoto();

  const [removeEventPagePhoto, { loading: isDeletingLegacyPhoto }] = useRemoveEventPagePhotoMutation({
    onError: () => {
      toast(error);
    },
    onCompleted: () => {
      toast(success);
      onDeletePhoto?.();
    },
  });

  const [uploadPhotoFromUrl, { loading: isUploadingMigratedPhoto }] = useUploadPhotoFromUrlMutation({
    onError: () => {
      toast(error);
    },
  });
  const [updatePageContainerPhoto, { loading: isUpdatingMigratedPhoto }] = useUpdateRegistryPageContainerPhotoMutation({
    onError: () => {
      toast(error);
    },
  });

  const isUpdating = isDeletingLegacyPhoto || isUpdatingLegacyPhoto || isUploadingMigratedPhoto || isUpdatingMigratedPhoto;

  const handleDeletePhoto = useCallback(async () => {
    if (isMigrated && migratedPageContainerId) {
      try {
        const { errors } = await updatePageContainerPhoto({
          variables: { payload: { id: migratedPageContainerId, mainPhotoId: null } },
        });
        if (errors) {
          toast(error);
          return;
        }
        toast(success);
        onDeletePhoto?.();
      } catch {
        // `onError` already toasted; just stop the success flow.
      }
      return;
    }
    if (!eventPageId) return;
    removeEventPagePhoto({
      variables: { eventPageId },
      update: cache => {
        updateLegacyPhotoInCache({ cache, url: null });
      },
    });
  }, [isMigrated, migratedPageContainerId, updatePageContainerPhoto, eventPageId, removeEventPagePhoto, updateLegacyPhotoInCache, toast, success, error, onDeletePhoto]);

  const { open: openFilePicker, getPhotoPreviewUrl } = useFilestack({
    containerId: firebaseId || '',
    onUploadDone: async (res): Promise<void> => {
      const { filename, mimetype, size, url } = res.filesUploaded[0];
      const previewUrl = await getPhotoPreviewUrl({ url });
      const loadImageDimensions = (imageUrl: string): Promise<{ width: number; height: number }> =>
        new Promise(resolve => {
          const image = new Image();

          image.onload = () => {
            const height = image.height;
            const width = image.width;
            resolve({ width, height });
          };

          image.src = imageUrl;
        });

      const dimensions = await loadImageDimensions(previewUrl);

      if (isMigrated && migratedPageContainerId) {
        try {
          const { data } = await uploadPhotoFromUrl({
            variables: { payload: { assetId: filename, containerId: migratedPageContainerId, url: previewUrl } },
          });
          const mainPhotoId = data?.uploadPhotoFromUrl?.id;
          if (!mainPhotoId) {
            toast(error);
            return;
          }
          const { errors } = await updatePageContainerPhoto({
            variables: { payload: { id: migratedPageContainerId, mainPhotoId } },
          });
          if (errors) {
            toast(error);
            return;
          }
          onUpdatePhoto?.(previewUrl);
          toast(success);
        } catch {
          // Both mutations have `onError` toasts; the catch just keeps the
          // promise from rejecting out of `onUploadDone`.
        }
        return;
      }

      if (!eventPageId) return;
      try {
        const { data } = await saveNativeEventPagePhoto({
          containerId: firebaseId,
          eventPageId,
          previewUrl,
          assetPayload: {
            assetId: filename,
            mimeType: mimetype,
            fileSize: size,
            width: dimensions.width,
            height: dimensions.height,
          },
          setAssetOptions: {
            update: cache => {
              updateLegacyPhotoInCache({ cache, url: previewUrl });
            },
          },
        });
        onUpdatePhoto?.(data?.setEventPageAsset?.photoV2?.url);
        toast(success);
      } catch {
        toast(error);
      }
    },
    maxFiles: 1,
  });

  const handleOnUpdatePhoto = useCallback(() => {
    openFilePicker();
  }, [openFilePicker]);

  const canShowEmptyCard = showEmptyCard && hasTarget;
  const placeholder = canShowEmptyCard ? (
    <IconCard
      onClick={handleOnUpdatePhoto}
      text="Set your photo"
      icon={<Photos />}
    />
  ) : (
    <Box __css={styles.emptyImage} />
  );
  const isEmptyCardVisible = !photo && canShowEmptyCard;

  return (
    <Box
      maxWidth={{ md: isEmptyCardVisible ? '100%' : '36rem' }}
      width={{ _: '100%', md: isEmptyCardVisible ? '100%' : '25.6rem' }}
      position="relative"
      opacity={isUpdating ? '0.3' : '1'}
    >
      {photo ? (
        <StyledImage
          ref={photoRef}
          src={photo}
          onLoad={handleImageLoad}
        />
      ) : (
        placeholder
      )}
      {isUpdating && <TempSpinner position={'absolute'} />}
      {!isEmptyCardVisible && canEdit && (
        <Box
          position="absolute"
          left={0}
          top={0}
          margin={6}
        >
          <PhotoMenu
            handleUpdatePhoto={handleOnUpdatePhoto}
            handleDeletePhoto={handleDeletePhoto}
            photoExists
          >
            <ButtonV2
              background="rgba(0, 0, 0, 0.80)"
              padding={4}
              borderRadius="100%"
              intent="neutral"
            >
              <CameraIcon />
            </ButtonV2>
          </PhotoMenu>
        </Box>
      )}
    </Box>
  );
};
